"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { m, useReducedMotion } from "motion/react";
import { useTranslation } from "@/lib/hooks/use-translation";

type Item = { q: string; a: string };

const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * The product pages' FAQ.
 *
 * The one interactive thing on either page, hence the only client component in
 * `components/product`. It is the home page's accordion behaviour without that
 * one's three-group stagger arithmetic — these lists are five flat questions,
 * so there are no groups to count through.
 *
 * The rule that matters is carried over intact: every answer stays mounted and
 * is opened by animating its own height, rather than being unmounted when
 * shut. `ProductStructuredData` publishes this same Q&A as FAQPage markup, and
 * Google requires the marked-up answers to be present in the page; the
 * crawlers behind AI answers read the HTML without running our JS, so an
 * unmounted answer would exist for nobody. `inert` keeps a shut answer out of
 * the tab order and the accessibility tree, which is all that hiding it needs
 * to mean.
 */
export default function ProductFaq({ ns }: Readonly<{ ns: "crm" | "pos" }>) {
  const { t } = useTranslation();
  const root = `product.${ns}.faq`;
  const items = t(`${root}.items`, { returnObjects: true }) as unknown as Item[];

  // The first answer is open on arrival, so the section lands showing what an
  // opened row looks like rather than five identical closed bars.
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section
      id="faq"
      aria-label={t(`${root}.aria`)}
      className="scroll-mt-13 bg-white"
    >
      <div className="mx-auto grid w-full max-w-[1440px] gap-10 px-4 py-14 md:px-[80px] md:py-[88px] lg:grid-cols-[400px_1fr] lg:gap-[72px]">
        <header className="flex flex-col gap-3">
          <h2 className="text-[26px]/[38px] font-semibold text-black sm:text-[32px]/[44px] lg:text-[40px]/[48px]">
            {t(`${root}.heading`)}
          </h2>
          <p className="text-[17px]/[28px] text-black/60 sm:text-[18px]/[28px]">
            {t(`${root}.sub`)}
          </p>
        </header>

        <div className="flex flex-col gap-3.5">
          {items.map((item, i) => (
            <Row
              key={item.q}
              item={item}
              panelId={`${ns}-faq-${i}`}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function Row({
  item,
  panelId,
  isOpen,
  onToggle,
}: Readonly<{ item: Item; panelId: string; isOpen: boolean; onToggle: () => void }>) {
  const reduced = useReducedMotion();

  return (
    <div className="overflow-hidden rounded-2xl border border-[#E6E6E6] bg-white">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        className={`flex w-full items-center justify-between gap-4 px-6 pt-5 text-left md:px-7 ${
          isOpen ? "pb-0" : "pb-5"
        }`}
      >
        <span className="text-[16px]/[26px] font-semibold text-[#16202E] sm:text-[18px]/[28px]">
          {item.q}
        </span>
        <ChevronDown
          aria-hidden
          strokeWidth={2.4}
          className={`size-6 flex-none text-[#6D717F] transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <m.div
        id={panelId}
        role="region"
        inert={!isOpen}
        // `false`, so the server's shut rows render at height 0 rather than
        // animating open on hydration, and the one open row paints open.
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: reduced ? 0 : 0.3, ease: EASE }}
        className="overflow-hidden"
      >
        <p className="px-6 pb-5 pt-3 text-[15px]/[26px] text-black/65 sm:text-[16px]/[26px] md:px-7">
          {item.a}
        </p>
      </m.div>
    </div>
  );
}
